import { useState, useRef, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../../utils/helpers'

/**
 * Tooltip Component
 * Displays a floating text box on hover using React Portal to escape parent clipping.
 * 
 * @param {Object} props
 * @param {string} props.text - The content of the tooltip
 * @param {React.ReactNode} props.children - The trigger element
 * @param {string} [props.className] - Additional classes for the trigger container
 */
export default function Tooltip({ text, children, className }) {
    const [isVisible, setIsVisible] = useState(false)
    const [style, setStyle] = useState({})
    const [arrowPosition, setArrowPosition] = useState({})
    const triggerRef = useRef(null)

    useLayoutEffect(() => {
        if (isVisible && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect()
            const tooltipWidth = 256 // w-64
            const padding = 16
            const screenWidth = window.innerWidth

            let left = rect.left + (rect.width / 2) // Start centered on trigger
            let xOffset = -50 // Start with -50% translateX (center)
            let arrowLeft = '50%'

            // Check Left Edge
            if (left - (tooltipWidth / 2) < padding) {
                left = padding + (tooltipWidth / 2) // Push right
                xOffset = -50 // Keep center transform, just moved origin
                // Strictly aligning to left edge might be better?
                // Let's stick to centering but clamping the 'left' value.
            }

            // Check Right Edge
            if (left + (tooltipWidth / 2) > screenWidth - padding) {
                left = screenWidth - padding - (tooltipWidth / 2)
            }

            // Calculate arrow offset relative to the new tooltip center
            // Actual center of trigger: rect.left + rect.width/2
            // Actual center of tooltip: left
            // Diff: (rect.left + rect.width/2) - left
            const offset = (rect.left + rect.width / 2) - left
            // Arrow needs to be shifted by this opposite amount to stay pointing at trigger
            arrowLeft = `calc(50% + ${offset}px)`


            setStyle({
                top: rect.top - 8, // 8px spacing above
                left: left,
                transform: `translateX(-50%) translateY(-100%)`
            })
            setArrowPosition({
                left: arrowLeft
            })
        }
    }, [isVisible])

    return (
        <div
            ref={triggerRef}
            className={cn("inline-flex items-center cursor-help", className)}
            onMouseEnter={() => setIsVisible(true)}
            onMouseLeave={() => setIsVisible(false)}
        >
            {children}

            {isVisible && createPortal(
                <div
                    className="fixed z-[9999] w-64 max-w-[90vw] px-3 py-2 bg-slate-800 text-white text-xs rounded shadow-xl pointer-events-none animate-fade-in break-words text-center"
                    style={style}
                >
                    {text}
                    {/* Arrow */}
                    <div
                        className="absolute top-full -mt-1 w-0 h-0 border-4 border-transparent border-t-slate-800"
                        style={arrowPosition}
                    ></div>
                </div>,
                document.body
            )}
        </div>
    )
}

/**
 * Info Icon Tooltip
 * A convenience component for a standard (i) icon with tooltip.
 */
export function InfoTooltip({ text, className }) {
    return (
        <Tooltip text={text} className={className}>
            <svg className="w-4 h-4 text-admin-muted hover:text-brand-navy transition-colors cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
        </Tooltip>
    )
}
